import type { ButtonHTMLAttributes } from 'react';
import React from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import type { VariantProp } from '../../common/colors';
import type { ShortcutKey } from '../../formatting/Shortcut/Shortcut';
import { Icon } from '../../assets/Icon/Icon';
import { fade, palette } from '../../common/colors';
import { devices } from '../../common/devices';
import { FocusRing } from '../../common/focus_rings';
import { selectors } from '../../common/selectors';
import { getVariantStyles, variantIconColor } from '../../common/variant';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import Shortcut from '../../formatting/Shortcut/Shortcut';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Text } from '../../text/Text';

const utilityControlCalloutPosition = '25%';

const UtilityControlCallout = styled('div', {
  position: 'absolute',
  top: `-${utilityControlCalloutPosition}`,
  right: `-${utilityControlCalloutPosition}`,
  widthAll: '$8',
  heightAll: '$8',
  background: variantIconColor,
  borderRadius: '99rem',
  transform: `translate(-${utilityControlCalloutPosition}, ${utilityControlCalloutPosition})`,

  variants: {
    variant: {
      alternative: {
        ...getVariantStyles('alternative'),
      },
      attention: {
        ...getVariantStyles('attention'),
      },
      brand: {
        ...getVariantStyles('brand'),
      },
      negative: {
        ...getVariantStyles('negative'),
      },
      neutral: {
        ...getVariantStyles('neutral'),
      },
      positive: {
        ...getVariantStyles('positive'),
      },
    },
  },
});
UtilityControlCallout.displayName = 'UtilityControlCallout';

const UtilityControlIcon = styled(Icon, {
  color: '$$iconColor',

  [devices.touch]: {
    size: '$12',
  },

  [devices.mouse]: {
    size: '$10',
  },
});
UtilityControlIcon.displayName = 'UtilityControlIcon';

const UtilityControlContainer = styled('button', FocusRing, {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  [devices.touch]: {
    size: '$28',
    borderRadius: '$8',
  },

  [devices.mouse]: {
    size: '$20',
    borderRadius: '$6',
  },

  variants: {
    disabled: {
      true: {
        pointerEvents: 'none',
      },
      false: {},
    },
    internal: {
      true: {},
      false: {},
    },
    variant: {
      alternative: {},
      attention: {},
      brand: {},
      negative: {},
      neutral: {},
      positive: {},
      application: {},
    },
  },

  compoundVariants: [
    {
      disabled: true,
      css: {
        opacity: 0.5,
        cursor: 'not-allowed',

        [selectors.hover]: {
          opacity: 0.5,
        },

        [selectors.focus]: {
          opacity: 0.5,
        },
      },
    },
    {
      disabled: false,
      variant: 'alternative',
      css: {
        backgroundColor: fade(palette.bgAlternativeLight, 0.7),
        strokeAll: colors.transparent,

        $$iconColor: colors.iconAlternativeLight,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.bgAlternativeDark, 0.7),
          strokeAll: colors.transparent,

          $$iconColor: colors.iconAlternativeDark,
        },

        [selectors.hover]: {
          backgroundColor: fade(palette.bgAlternativeLight, 0.9),
          strokeAll: colors.strokeAlternativeLight,

          $$iconColor: colors.bodyAlternativeLight,

          [darkThemeSelector]: {
            backgroundColor: fade(palette.bgAlternativeDark, 0.9),
            strokeAll: colors.strokeAlternativeDark,

            $$iconColor: colors.bodyAlternativeDark,
          },
        },

        [selectors.focus]: {
          backgroundColor: colors.bgAlternativeLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgAlternativeDark,
          },
        },
      },
    },
    {
      disabled: false,
      variant: 'attention',
      css: {
        backgroundColor: fade(palette.bgAttentionLight, 0.7),
        strokeAll: colors.transparent,

        $$iconColor: colors.iconAttentionLight,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.bgAttentionDark, 0.7),
          strokeAll: colors.transparent,

          $$iconColor: colors.iconAttentionDark,
        },

        [selectors.hover]: {
          backgroundColor: fade(palette.bgAttentionLight, 0.9),
          strokeAll: colors.strokeAttentionLight,

          $$iconColor: colors.bodyAttentionLight,

          [darkThemeSelector]: {
            backgroundColor: fade(palette.bgAttentionDark, 0.9),
            strokeAll: colors.strokeAttentionDark,

            $$iconColor: colors.bodyAttentionDark,
          },
        },

        [selectors.focus]: {
          backgroundColor: colors.bgAttentionLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgAttentionDark,
          },
        },
      },
    },
    {
      disabled: false,
      variant: 'brand',
      css: {
        backgroundColor: fade(palette.bgBrandLight, 0.7),
        strokeAll: colors.transparent,

        $$iconColor: colors.iconBrandLight,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.bgBrandDark, 0.7),
          strokeAll: colors.transparent,

          $$iconColor: colors.iconBrandDark,
        },

        [selectors.hover]: {
          backgroundColor: fade(palette.bgBrandLight, 0.9),
          strokeAll: colors.strokeBrandLight,

          $$iconColor: colors.bodyBrandLight,

          [darkThemeSelector]: {
            backgroundColor: fade(palette.bgBrandDark, 0.9),
            strokeAll: colors.strokeBrandDark,

            $$iconColor: colors.bodyBrandDark,
          },
        },

        [selectors.focus]: {
          backgroundColor: colors.bgBrandLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgBrandDark,
          },
        },
      },
    },
    {
      disabled: false,
      variant: 'negative',
      css: {
        backgroundColor: fade(palette.bgNegativeLight, 0.7),
        strokeAll: colors.transparent,

        $$iconColor: colors.iconNegativeLight,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.bgNegativeDark, 0.7),
          strokeAll: colors.transparent,

          $$iconColor: colors.iconNegativeDark,
        },

        [selectors.hover]: {
          backgroundColor: fade(palette.bgNegativeLight, 0.9),
          strokeAll: colors.strokeNegativeLight,

          $$iconColor: colors.bodyNegativeLight,

          [darkThemeSelector]: {
            backgroundColor: fade(palette.bgNegativeDark, 0.9),
            strokeAll: colors.strokeNegativeDark,

            $$iconColor: colors.bodyNegativeDark,
          },
        },

        [selectors.focus]: {
          backgroundColor: colors.bgNegativeLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgNegativeDark,
          },
        },
      },
    },
    {
      disabled: false,
      variant: 'neutral',
      css: {
        backgroundColor: fade(palette.bgNeutralLight, 0.7),
        strokeAll: colors.transparent,

        $$iconColor: colors.iconNeutralLight,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.bgNeutralDark, 0.7),
          strokeAll: colors.transparent,

          $$iconColor: colors.iconNeutralDark,
        },

        [selectors.hover]: {
          backgroundColor: fade(palette.bgNeutralLight, 0.9),
          strokeAll: colors.strokeNeutralLight,

          $$iconColor: colors.bodyNeutralLight,

          [darkThemeSelector]: {
            backgroundColor: fade(palette.bgNeutralDark, 0.9),
            strokeAll: colors.strokeNeutralDark,

            $$iconColor: colors.bodyNeutralDark,
          },
        },

        [selectors.focus]: {
          backgroundColor: colors.bgNeutralLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgNeutralDark,
          },
        },
      },
    },
    {
      disabled: false,
      variant: 'positive',
      css: {
        backgroundColor: fade(palette.bgPositiveLight, 0.7),
        strokeAll: colors.transparent,

        $$iconColor: colors.iconPositiveLight,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.bgPositiveDark, 0.7),
          strokeAll: colors.transparent,

          $$iconColor: colors.iconPositiveDark,
        },

        [selectors.hover]: {
          backgroundColor: fade(palette.bgPositiveLight, 0.9),
          strokeAll: colors.strokePositiveLight,

          $$iconColor: colors.bodyPositiveLight,

          [darkThemeSelector]: {
            backgroundColor: fade(palette.bgPositiveDark, 0.9),
            strokeAll: colors.strokePositiveDark,

            $$iconColor: colors.bodyPositiveDark,
          },
        },

        [selectors.focus]: {
          backgroundColor: colors.bgPositiveLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgPositiveDark,
          },
        },
      },
    },
    {
      disabled: false,
      variant: 'application',
      css: {
        backgroundColor: fade(palette.bgApplicationLight, 0.7),
        strokeAll: colors.transparent,

        $$iconColor: colors.iconNeutralLight,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.bgApplicationDark, 0.7),
          strokeAll: colors.transparent,

          $$iconColor: colors.iconNeutralDark,
        },

        [selectors.hover]: {
          backgroundColor: fade(palette.bgNeutralLight, 0.7),
          strokeAll: fade(palette.strokeNeutralLight, 0.7),

          $$iconColor: colors.bodyNeutralLight,

          [darkThemeSelector]: {
            backgroundColor: fade(palette.bgNeutralDark, 0.7),
            strokeAll: fade(palette.strokeNeutralDark, 0.7),

            $$iconColor: colors.bodyNeutralDark,
          },
        },

        [selectors.focus]: {
          backgroundColor: colors.bgNeutralLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgNeutralDark,
          },
        },
      },
    },
    {
      internal: true,
      css: {
        backgroundColor: fade(palette.internalBgLight, 0.7),
        strokeAll: colors.transparent,

        $$iconColor: colors.internalIconLight,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.internalBgDark, 0.7),
          strokeAll: colors.transparent,

          $$iconColor: colors.internalIconDark,
        },

        [selectors.hover]: {
          backgroundColor: fade(palette.internalBgLight, 0.9),
          strokeAll: colors.internalStrokeLight,

          $$iconColor: colors.internalBodyLight,

          [darkThemeSelector]: {
            backgroundColor: fade(palette.internalBgDark, 0.9),
            strokeAll: colors.internalStrokeDark,

            $$iconColor: colors.internalBodyDark,
          },
        },

        [selectors.focus]: {
          backgroundColor: colors.internalBgLight,

          [darkThemeSelector]: {
            backgroundColor: colors.internalBgDark,
          },
        },
      },
    },
  ],
});
UtilityControlContainer.displayName = 'UtilityControlContainer';

export type UtilityControlProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /**
   * Whether the control is currently active (being interacted with).
   */
  active?: boolean;
  /**
   * The callout variant of the control.
   */
  callout?: VariantProp;
  /**
   * Whether the control is disabled.
   */
  disabled?: boolean;
  /**
   * The icon to display in the control.
   */
  icon?: IconName;
  /**
   * Whether the control is internal.
   */
  internal?: boolean;
  /**
   * Required! The label of the control.
   */
  label: React.ReactNode;
  /**
   * The event handler for the control.
   */
  onClick?: React.ReactEventHandler;
  /**
   * Set shortcut keys for the control.
   */
  shortcut?: ShortcutKey[];
  /**
   * The variant of the control.
   */
  variant: VariantProp | 'application';
  /**
   * The ref of the control.
   */
  ref?: React.Ref<HTMLButtonElement | null>;
};

export function UtilityControl({
  callout,
  disabled = false,
  icon = 'cross',
  internal = false,
  active = false,
  label,
  onClick,
  shortcut,
  variant = 'neutral',
  ref,
  ...remaining
}: UtilityControlProps) {
  const content = (
    <UtilityControlContainer
      ref={ref}
      type="button"
      onClick={!disabled ? onClick : undefined}
      tabIndex={!disabled ? 0 : -1}
      aria-label={`${label}`}
      disabled={disabled}
      internal={internal}
      variant={variant}
      {...remaining}
    >
      {callout != null && <UtilityControlCallout variant={callout} />}
      <UtilityControlIcon icon={icon} />
    </UtilityControlContainer>
  );

  if (disabled || active) {
    return content;
  }

  return (
    <Tooltip
      contents={
        <Text>
          {label} {shortcut && <Shortcut keys={shortcut} />}
        </Text>
      }
      toggle={false}
    >
      {content}
    </Tooltip>
  );
}
